import { getApiClient } from './authApiClient';

export const GALLAGHER_BROKER_TENANT_ID = 'f9471bc0-c3b6-4f52-a0cd-ec6a328e54bb';
export const BROKER_COMPARISON_BATCH_SIZE = 50;
const HASH = /^[a-f0-9]{64}$/;
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const sameKeys = (value, keys) => object(value) && Object.keys(value).length === keys.length
  && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
const text = (value, max = 500) => typeof value === 'string' && value.length > 0 && value.length <= max
  && !/[\x00-\x1f\x7f]/.test(value); // eslint-disable-line no-control-regex
export const validBrokerComparisonId = value => text(value, 200) && value.trim() === value && !/[\\/,]/.test(value);
const nullableText = (value, max) => value === null || text(value, max);
const currency = value => typeof value === 'string' && /^[A-Z]{3}$/.test(value);
const nullableCurrency = value => value === null || currency(value);
const canonicalTimestamp = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  && Number.isFinite(Date.parse(value)) && new Date(value).toISOString() === value;
const nullableDate = value => value === null || canonicalTimestamp(value)
  || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    && Number.isFinite(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value);
const references = value => Array.isArray(value) && value.length <= 2000 && value.every(item => text(item, 1000));
const amount = value => typeof value === 'string' && value.length <= 100 && /^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value);
const failure = (code, message, status) => Object.assign(new Error(message), { code, ...(status ? { status } : {}) });
const invalid = () => failure('INVALID_LIMIT_COMPARISONS', 'The insurer limit response could not be verified. Reload insurer limits.', 503);
const requireComparison = condition => { if (!condition) throw invalid(); };
const CAPABILITY_KEYS = ['canReadLimitComparisons', 'canPreviewCarrierRequest', 'canSendCarrierRequest', 'sendDisabledReason'];

export const equalBrokerComparisonCapabilities = (a, b) => object(a) && object(b)
  && CAPABILITY_KEYS.every(key => a[key] === b[key]);

function validCapabilities(value) {
  return sameKeys(value, CAPABILITY_KEYS) && value.canReadLimitComparisons === true
    && typeof value.canPreviewCarrierRequest === 'boolean' && typeof value.canSendCarrierRequest === 'boolean'
    && (!value.canSendCarrierRequest || value.canPreviewCarrierRequest)
    && (value.canSendCarrierRequest ? value.sendDisabledReason === null : text(value.sendDisabledReason, 1000));
}

function validateLimit(value, approved = false) {
  requireComparison(sameKeys(value, ['state', 'amount', 'currency', 'reason', 'sourceRecords', 'asOf', ...(approved ? ['approvalStatus'] : [])])
    && ['known', 'unknown'].includes(value.state) && nullableText(value.reason, 1000)
    && references(value.sourceRecords) && nullableDate(value.asOf)
    && (value.state === 'known' ? amount(value.amount) && currency(value.currency) : value.amount === null && nullableCurrency(value.currency))
    && (!approved || nullableText(value.approvalStatus, 100)));
}

function validateResponse(payload, expected) {
  requireComparison(sameKeys(payload, ['success', 'data']) && payload.success === true);
  const data = payload.data;
  requireComparison(sameKeys(data, ['tenantId', 'principalId', 'comparisons', 'capabilities'])
    && data.tenantId === expected.tenantId && data.principalId === expected.requesterId
    && validCapabilities(data.capabilities) && Array.isArray(data.comparisons)
    && data.comparisons.length === expected.companyIds.length);
  const requested = new Set(expected.companyIds);
  const seen = new Set();
  for (const row of data.comparisons) {
    requireComparison(sameKeys(row, ['tenantId', 'companyId', 'companyName', 'insuredLimit', 'limitComparison', 'sourceRevision', 'freshness', 'capabilities'])
      && row.tenantId === expected.tenantId && requested.has(row.companyId) && !seen.has(row.companyId)
      && nullableText(row.companyName, 1000) && typeof row.sourceRevision === 'string' && HASH.test(row.sourceRevision)
      && validCapabilities(row.capabilities) && equalBrokerComparisonCapabilities(row.capabilities, data.capabilities));
    seen.add(row.companyId);
    const insured = row.insuredLimit;
    requireComparison(sameKeys(insured, ['state', 'amount', 'currency', 'reason', 'asOf', 'effectiveFrom', 'effectiveTo', 'insurerId', 'sourceRefs', 'sourceRecords'])
      && ['known', 'unknown', 'expired', 'withdrawn', 'declined', 'not_yet_effective', 'conflicting'].includes(insured.state)
      && nullableText(insured.reason, 1000) && nullableText(insured.insurerId, 200)
      && nullableDate(insured.asOf) && nullableDate(insured.effectiveFrom) && nullableDate(insured.effectiveTo)
      && references(insured.sourceRefs) && references(insured.sourceRecords)
      && (insured.state === 'known' ? amount(insured.amount) && currency(insured.currency) : insured.amount === null && nullableCurrency(insured.currency)));
    requireComparison(sameKeys(row.limitComparison, ['requested', 'approved', 'approvalStatus']));
    validateLimit(row.limitComparison.requested);
    validateLimit(row.limitComparison.approved, true);
    requireComparison(row.limitComparison.approvalStatus === row.limitComparison.approved.approvalStatus);
    requireComparison(sameKeys(row.freshness, ['checkedAt', 'sourceRevision', 'readOnly', 'persisted'])
      && canonicalTimestamp(row.freshness.checkedAt) && row.freshness.sourceRevision === row.sourceRevision
      && row.freshness.readOnly === true && row.freshness.persisted === false);
  }
  return data;
}

const ERROR_MESSAGES = {
  400: 'The insurer limit request is invalid.',
  401: 'Sign in again to review insurer limits.',
  403: 'Current access to these insurer limits is not permitted.',
  404: 'A requested company was not found in this client book.',
  409: 'Insurer limit sources changed. Reload insurer limits.',
  503: 'Insurer limits are currently unavailable. Reload insurer limits.',
};

// The requester identity is an expected response binding, never a claimed server identity.
export async function getBrokerLimitComparisons(tenantId, companyIds, { requesterId, signal } = {}) {
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('viewAsUser')) {
    throw failure('SUPPORT_PREVIEW_UNSUPPORTED', 'Exit support preview to review broker insurer limits.', 400);
  }
  if (tenantId !== GALLAGHER_BROKER_TENANT_ID || !validBrokerComparisonId(requesterId)
    || !Array.isArray(companyIds) || companyIds.length < 1 || companyIds.length > BROKER_COMPARISON_BATCH_SIZE
    || companyIds.some(value => !validBrokerComparisonId(value)) || new Set(companyIds).size !== companyIds.length) {
    throw failure('INVALID_LIMIT_COMPARISON_SCOPE', ERROR_MESSAGES[400], 400);
  }
  if (signal?.aborted) throw Object.assign(failure('ERR_CANCELED', 'Insurer limit review was canceled.'), { name: 'AbortError' });
  let response;
  try {
    response = await getApiClient('company').get(`/integrated-dcl/tenants/${encodeURIComponent(tenantId)}/limit-comparisons`, {
      params: { companyIds: companyIds.join(',') }, signal,
    });
  } catch (error) {
    if (signal?.aborted || error?.code === 'ERR_CANCELED' || error?.name === 'AbortError') {
      throw Object.assign(failure('ERR_CANCELED', 'Insurer limit review was canceled.'), { name: 'AbortError' });
    }
    const status = Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, error?.response?.status) ? error.response.status : 503;
    throw failure('LIMIT_COMPARISONS_UNAVAILABLE', ERROR_MESSAGES[status], status);
  }
  if (signal?.aborted) throw Object.assign(failure('ERR_CANCELED', 'Insurer limit review was canceled.'), { name: 'AbortError' });
  return validateResponse(response?.data, { tenantId, companyIds, requesterId });
}
